/**
 * The single source of truth for extraction-confidence tiers.
 *
 * Thresholds (rebrand): >= .85 high · >= .70 mid · else low · null/undefined none.
 * Every confidence surface — chip, ring, spine, badge — MUST consume this helper
 * so the colour language stays coherent. Do not re-derive thresholds elsewhere.
 *
 * Mirrors `confTier` in design_handoff_trace/ui.jsx.
 */
export type ConfTier = "high" | "mid" | "low" | "none";

export const CONF_HIGH = 0.85;
export const CONF_MID = 0.7;

export function confTier(v: number | null | undefined): ConfTier {
  if (v == null || Number.isNaN(v)) return "none";
  if (v >= CONF_HIGH) return "high";
  if (v >= CONF_MID) return "mid";
  return "low";
}

/** Tier → TRACE colour token (tokens only — never a hardcoded hex). */
export const CONF_TOKEN: Record<ConfTier, string> = {
  high: "var(--tr-ok)",
  mid: "var(--tr-warn)",
  low: "var(--tr-bad)",
  none: "var(--tr-text-4)",
};

export function confColorVar(v: number | null | undefined): string {
  return CONF_TOKEN[confTier(v)];
}

/** "86%" / "100%" / "—" (em-dash when there is no score). */
export function confPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Math.round(v * 100)}%`;
}

const CONF_LABEL: Record<ConfTier, string> = {
  high: "High confidence",
  mid: "Needs review",
  low: "Low confidence",
  none: "No score",
};

export function confLabel(v: number | null | undefined): string {
  return CONF_LABEL[confTier(v)];
}
